import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  Globe, Plus, Trash2, RefreshCw, CheckCircle, AlertTriangle, Clock,
  Loader2, Copy, ExternalLink, Shield, X, Info
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

export default function CustomDomainsManager() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [adding, setAdding] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${API}/custom-domains`);
      setDomains(response.data.domains || []);
    } catch (error) {
      console.error('Failed to fetch custom domains:', error);
      toast.error('Failed to load custom domains');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      toast.error('Please enter a domain');
      return;
    }

    setAdding(true);
    try {
      const response = await axios.post(`${API}/custom-domains`, {
        custom_domain: newDomain.trim(),
        display_name: displayName.trim() || null
      });
      
      toast.success('Custom domain added successfully');
      setAddModalOpen(false);
      setNewDomain('');
      setDisplayName('');
      fetchDomains();
      
      // Show the newly added domain details
      setSelectedDomain(response.data);
    } catch (error) {
      console.error('Failed to add domain:', error);
      toast.error(error.response?.data?.detail || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const handleVerifyDomain = async (domainId) => {
    setVerifyingId(domainId);
    try {
      const response = await axios.post(`${API}/custom-domains/${domainId}/verify`);
      
      if (response.data.verified) {
        toast.success('Domain verified and activated!');
      } else {
        toast.error(response.data.error || 'DNS verification failed. Check your CNAME configuration.');
      }
      
      fetchDomains();
    } catch (error) {
      console.error('Failed to verify domain:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify domain');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Are you sure you want to delete this custom domain?')) return;
    
    setDeletingId(domainId);
    try {
      await axios.delete(`${API}/custom-domains/${domainId}`);
      toast.success('Domain deleted successfully');
      fetchDomains();
      if (selectedDomain?.id === domainId) {
        setSelectedDomain(null);
      }
    } catch (error) {
      console.error('Failed to delete domain:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete domain');
    } finally {
      setDeletingId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getStatusBadge = (domain) => {
    if (domain.status === 'active') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" />
          Active
        </span>
      );
    }
    if (domain.dns_status === 'verified') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
          <Shield className="w-3 h-3" />
          DNS Verified
        </span>
      );
    }
    if (domain.dns_status === 'verifying') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
          <Loader2 className="w-3 h-3 animate-spin" />
          Verifying
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-700">
        <Clock className="w-3 h-3" />
        Pending DNS
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="custom-domains-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Custom Domains
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Serve your Trust Center from your own custom domain
          </p>
        </div>
        <Button 
          onClick={() => setAddModalOpen(true)}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="add-domain-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Domain
        </Button>
      </div>

      {/* Domains List */}
      {domains.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-12 text-center">
            <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Custom Domains</h3>
            <p className="text-slate-600 mb-4">
              Add a custom domain to serve your Trust Center from your own subdomain.
            </p>
            <Button onClick={() => setAddModalOpen(true)} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Domain
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {domains.map((domain) => (
            <Card 
              key={domain.id} 
              className={`bg-white border-slate-200 hover:shadow-md transition-shadow cursor-pointer ${
                selectedDomain?.id === domain.id ? 'ring-2 ring-teal-500' : ''
              }`}
              onClick={() => setSelectedDomain(domain)}
              data-testid={`domain-card-${domain.custom_domain}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900">{domain.custom_domain}</h3>
                      {getStatusBadge(domain)}
                    </div>
                    {domain.display_name && (
                      <p className="text-sm text-slate-600 mb-2">{domain.display_name}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>Created: {new Date(domain.created_at).toLocaleDateString()}</span>
                      {domain.dns_verified_at && (
                        <span>Verified: {new Date(domain.dns_verified_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {domain.status === 'active' && (
                      <a
                        href={`https://${domain.custom_domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                        title="Visit domain"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    {domain.status !== 'active' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVerifyDomain(domain.id)}
                        disabled={verifyingId === domain.id}
                        className="text-slate-700"
                        data-testid={`verify-btn-${domain.id}`}
                      >
                        {verifyingId === domain.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Verify DNS
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteDomain(domain.id)}
                      disabled={deletingId === domain.id}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-btn-${domain.id}`}
                    >
                      {deletingId === domain.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Selected Domain Details Panel */}
      {selectedDomain && (
        <Card className="bg-slate-50 border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-slate-900">
                DNS Configuration for {selectedDomain.custom_domain}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDomain(null)}
                className="text-slate-500"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <CardDescription className="text-slate-600">
              Follow these steps to configure your custom domain
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* DNS Instructions */}
            {selectedDomain.dns_instructions && (
              <>
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4 text-teal-600" />
                    Step 1: Add DNS Record
                  </h4>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2 font-mono text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Type:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-900 font-semibold">{selectedDomain.dns_instructions.record_type}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard(selectedDomain.dns_instructions.record_type)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Host/Name:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-900 font-semibold">{selectedDomain.dns_instructions.host}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard(selectedDomain.dns_instructions.host)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Points to/Value:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-900 font-semibold">{selectedDomain.dns_instructions.value}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard(selectedDomain.dns_instructions.value)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">TTL:</span>
                      <span className="text-slate-900">{selectedDomain.dns_instructions.ttl}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-teal-600" />
                    Step 2: Verify DNS
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-600">
                    {selectedDomain.dns_instructions.verification_steps?.map((step, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {selectedDomain.status !== 'active' && (
                    <Button
                      onClick={() => handleVerifyDomain(selectedDomain.id)}
                      disabled={verifyingId === selectedDomain.id}
                      className="mt-4 bg-teal-600 hover:bg-teal-700 text-white"
                      data-testid="verify-dns-panel-btn"
                    >
                      {verifyingId === selectedDomain.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Verify DNS Configuration
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Important Note */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Important Note
                  </h4>
                  <p className="text-sm text-amber-800">
                    DNS changes can take up to 24-48 hours to propagate globally. If verification fails, 
                    please wait and try again later. Make sure you've added the CNAME record exactly as shown above.
                  </p>
                  <p className="text-sm text-amber-800 mt-2">
                    <strong>SSL Note:</strong> After DNS verification, SSL certificates will be automatically 
                    provisioned. This may take a few additional minutes.
                  </p>
                </div>
              </>
            )}

            {/* Verification Status */}
            <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
              <span className="text-sm text-slate-600">Current Status:</span>
              {getStatusBadge(selectedDomain)}
              {selectedDomain.dns_resolved_to && (
                <span className="text-xs text-slate-500">
                  Resolves to: {selectedDomain.dns_resolved_to}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Domain Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Add Custom Domain
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Enter the custom domain you want to use for your Trust Center.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="domain" className="text-slate-700">Custom Domain</Label>
              <Input
                id="domain"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value.toLowerCase().trim())}
                placeholder="trust.yourcompany.com"
                className="mt-1 bg-white border-slate-300 text-slate-900"
                data-testid="new-domain-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Example: trust.example.com, security.yourcompany.com
              </p>
            </div>
            
            <div>
              <Label htmlFor="displayName" className="text-slate-700">Display Name (Optional)</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Customer Trust Portal"
                className="mt-1 bg-white border-slate-300 text-slate-900"
                data-testid="display-name-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                A friendly name to identify this domain
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setAddModalOpen(false)}
              className="border-slate-300 text-slate-900"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddDomain}
              disabled={adding || !newDomain.trim()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="confirm-add-domain-btn"
            >
              {adding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Domain
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
